import { zodResolver } from '@hookform/resolvers/zod';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledField, DistanceUnit } from 'remote/global';
import { usePutOperatorsVehiclesMutation } from 'api/queries/operators';
import { Button } from '@mui/material';
import { useOperatorDistanceUnit } from 'api/queries/users';
import { VehicleDetailsSchemaType, vehicleDetailsSchema } from './types';
import { VehicleDtoSchema } from '../BookingFormFactory/types';

export function VehicleDetailsFormFields({
  form,
  focusInitial,
}: {
  form: UseFormReturn<VehicleDetailsSchemaType>;
  focusInitial?: boolean;
}) {
  const { control } = form;
  const distanceUnit = useOperatorDistanceUnit();
  return (
    <>
      <ControlledField control={control} name='licensePlate' focusInitial={focusInitial} />
      <ControlledField control={control} name='make' />
      <ControlledField control={control} name='model' />
      <ControlledField control={control} name='year' />
      <ControlledField control={control} name='lengthMeters' />
      <ControlledField
        control={control}
        name='mileage'
        translationKey={distanceUnit === DistanceUnit.KM ? 'mileageKm' : 'mileageMi'}
      />
      <ControlledField control={control} name='seats' />
      <ControlledField control={control} name='standingPlaces' />
    </>
  );
}

function VehicleDetailsForm({
  defaultValues,
  vehicle,
}: {
  defaultValues?: VehicleDtoSchema;
  vehicle: VehicleDtoSchema;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<VehicleDetailsSchemaType>({
    defaultValues: {
      // TODO: Find out a better solution for assigning 'undefined' to a initial controlled value
      lengthMeters: '' as any, // Makes sure that field is controlled
      licensePlate: '',
      make: '',
      mileage: '' as any,
      model: '',
      seats: '' as any,
      standingPlaces: '' as any,
      year: '' as any,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleDetailsSchema),
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const putOperatorsVehiclesMutation = usePutOperatorsVehiclesMutation();

  const onValid = (data: VehicleDetailsSchemaType) =>
    putOperatorsVehiclesMutation.mutateAsync({
      ...vehicle,
      ...data,
    });

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleDetailsFormFields form={formInstance} />
      <Button
        type='submit'
        variant='contained'
        disabled={isSubmitting || isValidating || isLoading}
        fullWidth
        sx={{ mt: '2rem' }}
      >
        {t('save')}
      </Button>
    </form>
  );
}

export default VehicleDetailsForm;
